import { useQuery } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { RecordDrawer } from "@/components/RecordDrawer";
import { Badge, Button, Card, Empty, ErrorNote, Spinner } from "@/components/ui";
import { api, type BuyerRow, type BuyerUpsert, type InvestorRow, type InvestorUpsert } from "@/lib/api";
import { BUYER_FIELDS, INVESTOR_FIELDS } from "@/lib/records";
import { humanise } from "@/lib/dealStages";
import { formatAcres, formatDate, formatDue, formatMoneyCents, formatPhone } from "@/lib/format";

/** §7.7 investors. */
export function Investors() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["investors"],
    queryFn: () => api.investors(),
  });
  // v2.1 T6.1: add and edit in one drawer.
  const [drawer, setDrawer] = useState<InvestorRow | "new" | null>(null);
  if (isLoading) return <Spinner />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  return (
    <div className="p-4">
      <header className="mb-3 flex items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Investors</h1>
          <p className="text-xs text-fg-muted">
            {data.length} on the list ·{" "}
            {formatMoneyCents(data.reduce((sum, i) => sum + i.committed_cents, 0))} committed
          </p>
        </div>
        <Button size="sm" variant="primary" data-testid="investor-add" onClick={() => setDrawer("new")}>
          <Plus className="size-3.5" aria-hidden /> Add investor
        </Button>
      </header>
      {drawer && (
        <RecordDrawer
          title={drawer === "new" ? "Add investor" : `Edit ${drawer.name}`}
          fields={INVESTOR_FIELDS}
          initial={drawer === "new" ? undefined : drawer}
          submitLabel={drawer === "new" ? "Add investor" : "Save"}
          testId="investor-drawer"
          invalidate={["investors"]}
          onClose={() => setDrawer(null)}
          onSubmit={(values) =>
            drawer === "new"
              ? api.createInvestor(values as InvestorUpsert)
              : api.updateInvestor(drawer.id, values as InvestorUpsert)
          }
        />
      )}
      <Card className="overflow-hidden">
        {data.length === 0 ? (
          <Empty title="No investors yet." />
        ) : (
          <div className="-mx-3 -my-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="investors-table">
              <thead>
                <tr className="border-b bg-surface-2 text-left text-xs font-semibold">
                  <th className="px-2 py-1.5">Name</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5 text-right">Check size</th>
                  <th className="px-2 py-1.5 text-right">Committed</th>
                  <th className="px-2 py-1.5 text-right">Deals</th>
                  <th className="px-2 py-1.5">Terms</th>
                  <th className="px-2 py-1.5">Next touch</th>
                </tr>
              </thead>
              <tbody>
                {data.map((i) => (
                  <tr key={i.id} className="border-b last:border-b-0 hover:bg-surface-2">
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        className="float-right rounded p-0.5 text-fg-muted hover:text-fg"
                        aria-label={`Edit ${i.name}`}
                        data-testid={`investor-edit-${i.id}`}
                        onClick={() => setDrawer(i)}
                      >
                        <Pencil className="size-3.5" aria-hidden />
                      </button>
                      <span className="font-medium">{i.name}</span>
                      {i.entity && <span className="block text-xs text-fg-muted">{i.entity}</span>}
                      <span className="block text-xs text-fg-muted">
                        {formatPhone(i.phone_e164)}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs">{humanise(i.type)}</td>
                    <td className="px-2 py-1.5">
                      <Badge
                        tone={
                          i.status === "funded" || i.status === "committed"
                            ? "ok"
                            : i.status === "passed"
                              ? "danger"
                              : "muted"
                        }
                      >
                        {humanise(i.status)}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs tabular-nums">
                      {formatMoneyCents(i.check_min_cents)} – {formatMoneyCents(i.check_max_cents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatMoneyCents(i.committed_cents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{i.deals_funded}</td>
                    <td className="px-2 py-1.5 text-xs">{i.rate_terms ?? "—"}</td>
                    <td className="px-2 py-1.5 text-xs tabular-nums">
                      {i.touch_overdue ? (
                        <Badge tone="danger" data-testid="investor-overdue">
                          {formatDue(i.next_touch_at)}
                        </Badge>
                      ) : (
                        formatDue(i.next_touch_at)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** §7.7 buyers. */
export function Buyers() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["buyers"],
    queryFn: () => api.buyers(),
  });
  const [drawer, setDrawer] = useState<BuyerRow | "new" | null>(null);
  if (isLoading) return <Spinner />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  return (
    <div className="p-4">
      <header className="mb-3 flex items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Buyers</h1>
          <p className="text-xs text-fg-muted">{data.length} on the list</p>
        </div>
        <Button size="sm" variant="primary" data-testid="buyer-add" onClick={() => setDrawer("new")}>
          <Plus className="size-3.5" aria-hidden /> Add buyer
        </Button>
      </header>
      {drawer && (
        <RecordDrawer
          title={drawer === "new" ? "Add buyer" : `Edit ${drawer.name}`}
          fields={BUYER_FIELDS}
          initial={drawer === "new" ? undefined : drawer}
          submitLabel={drawer === "new" ? "Add buyer" : "Save"}
          testId="buyer-drawer"
          invalidate={["buyers"]}
          onClose={() => setDrawer(null)}
          onSubmit={(values) =>
            drawer === "new"
              ? api.createBuyer(values as BuyerUpsert)
              : api.updateBuyer(drawer.id, values as BuyerUpsert)
          }
        />
      )}
      <Card className="overflow-hidden">
        {data.length === 0 ? (
          <Empty title="No buyers yet." />
        ) : (
          <div className="-mx-3 -my-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="buyers-table">
              <thead>
                <tr className="border-b bg-surface-2 text-left text-xs font-semibold">
                  <th className="px-2 py-1.5">Name</th>
                  <th className="px-2 py-1.5">Type</th>
                  <th className="px-2 py-1.5">Where</th>
                  <th className="px-2 py-1.5">Size</th>
                  <th className="px-2 py-1.5">Cash</th>
                  <th className="px-2 py-1.5 text-right">Score</th>
                  <th className="px-2 py-1.5">Last purchase</th>
                </tr>
              </thead>
              <tbody>
                {data.map((b) => (
                  <tr key={b.id} className="border-b last:border-b-0 hover:bg-surface-2">
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        className="float-right rounded p-0.5 text-fg-muted hover:text-fg"
                        aria-label={`Edit ${b.name}`}
                        data-testid={`buyer-edit-${b.id}`}
                        onClick={() => setDrawer(b)}
                      >
                        <Pencil className="size-3.5" aria-hidden />
                      </button>
                      <span className="font-medium">{b.name}</span>
                      {b.entity && <span className="block text-xs text-fg-muted">{b.entity}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-xs">{humanise(b.type)}</td>
                    <td className="px-2 py-1.5 text-xs text-fg-muted">
                      {b.states.join(", ")}
                      {b.counties.length > 0 && ` · ${b.counties.join(", ")}`}
                    </td>
                    <td className="px-2 py-1.5 text-xs tabular-nums">
                      {formatAcres(b.min_acres)} – {formatAcres(b.max_acres)}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge tone={b.cash ? "ok" : "muted"}>{b.cash ? "cash" : "financed"}</Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{b.score ?? "—"}</td>
                    <td className="px-2 py-1.5 text-xs">{formatDate(b.last_purchase_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
